import React, {FC} from "react"

import GenericModal from "@irati/ui/src/GenericModal"
import Flex from "@irati/ui/src/Flex"
import {H2, H6, Body} from "@irati/ui/src/Typography"
import {FormattedMessage, useIntl} from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Button from "@irati/ui/src/Button"
import mainTheme from "@irati/ui/src/global/theme"
import {BulkBudgetAssignmentErrorType} from "@irati/types"
import downloadCsv from "@irati/utils/downloadCsv"
import {DistributeRewardRecipient} from "../../../enums/DistributeRewardTypes"
import {useRewardsDistribution, useRewardsDistributionActions} from "../../../stores/DistributeReward/hooks"

interface Props {
  resetCSV: () => void
  // eslint-disable-next-line no-undef
  payload: DistributeRewardState
}

const CSVErrorReportModal: FC<Props> = ({resetCSV}) => {
  const {dispatch} = useRewardsDistributionActions()
  const {formatMessage} = useIntl()
  const payload = useRewardsDistribution()
  const failedEmails =
    payload.recipientType === DistributeRewardRecipient.MANAGER
      ? payload.bulkBudgetAssignmentState.failedEmails
      : payload.bulkRewardState.failedEmails
  const totalEmails = payload.userEmails.length
  const successEmails = totalEmails - failedEmails.length
  const errorReportListCsvRows = failedEmails.map(({email, status, errorType}) => [email, status || errorType])
  const errorReport = {
    invalidUsers: 0,
    invalidBudgets: [],
    insufficientFundBudgets: [],
    invalidManagers: 0
  }
  failedEmails.forEach(({errorType, budget}) => {
    if (!errorType || errorType === BulkBudgetAssignmentErrorType.InvalidUser) {
      errorReport.invalidUsers += 1
    } else if (errorType === BulkBudgetAssignmentErrorType.InvalidBudget && !errorReport.invalidBudgets.includes(budget)) {
      errorReport.invalidBudgets.push(budget)
    } else if (errorType === BulkBudgetAssignmentErrorType.InsufficientFunds && !errorReport.insufficientFundBudgets.includes(budget)) {
      errorReport.insufficientFundBudgets.push(budget)
    } else if (errorType === BulkBudgetAssignmentErrorType.NotManager) {
      errorReport.invalidManagers += 1
    }
  })
  const onClose = () => {
    dispatch({type: "ON_REJECT_EMAIL_ERRORS"})
  }

  const onUploadNewCsv = () => {
    dispatch({type: "ON_REJECT_EMAIL_ERRORS"})
    resetCSV()
  }

  const onContinue = () => {
    dispatch({type: "ON_ACCEPT_EMAIL_ERRORS"})
  }

  const downloadCsvFile = () => {
    const headers: string = [formatMessage({id: "platform.email.label"}), formatMessage({id: "platform.status.label"})].join(",")

    // \ufeff needed to specify the csv may contain utf-8 characters
    let csv = `\ufeff${headers}`
    errorReportListCsvRows.forEach((row) => {
      csv += `\n${row}`
    })
    const fileName = formatMessage({id: "wallet.adminReward.bulkRewards.errorModal.downloadReport.csv.title"})
    downloadCsv(csv, fileName)
  }

  if (failedEmails.length <= 0) {
    return null
  }

  return (
    <GenericModal hideActions show onHide={onClose}>
      <H2 marginBottom={2}>
        <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.title" />
      </H2>

      <Flex direction="column" marginTop={4} marginBottom={4} marginLeft={2}>
        <Flex alignItems="center" marginBottom>
          <Icon icon={IconTypes.circleTickFill} marginRight />
          <Body>
            <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.matchedEmployees" values={{count: successEmails}} />
          </Body>
        </Flex>

        {errorReport.invalidUsers > 0 && (
          <Flex alignItems="center" marginBottom>
            <Icon icon={IconTypes.circleAlertFill} marginRight />
            <Body color={mainTheme.colors.tertiary.r_400}>
              <FormattedMessage
                id="wallet.adminReward.bulkRewards.errorModal.notMatchedEmployees"
                values={{count: errorReport.invalidUsers}}
              />
            </Body>
          </Flex>
        )}
        {errorReport.invalidManagers > 0 && (
          <Flex alignItems="center" marginBottom>
            <Icon icon={IconTypes.circleAlertFill} marginRight />
            <Body color={mainTheme.colors.tertiary.r_400}>
              <FormattedMessage
                id="wallet.adminReward.bulkRewards.errorModal.notMatchedManagers"
                values={{count: errorReport.invalidManagers}}
              />
            </Body>
          </Flex>
        )}
        {errorReport.invalidBudgets.length > 0 && (
          <Flex alignItems="center" marginBottom>
            <Icon icon={IconTypes.circleAlertFill} marginRight />
            <Body color={mainTheme.colors.tertiary.r_400}>
              <FormattedMessage
                id="wallet.adminReward.bulkRewards.errorModal.notMatchedBudgets"
                values={{count: errorReport.invalidBudgets.length}}
              />
            </Body>
          </Flex>
        )}
        {errorReport.insufficientFundBudgets.length > 0 && (
          <Flex alignItems="center" marginBottom>
            <Icon icon={IconTypes.circleAlertFill} marginRight />
            <Body color={mainTheme.colors.tertiary.r_400}>
              <FormattedMessage
                id="wallet.adminReward.bulkRewards.errorModal.notEnoughBudgets"
                values={{count: errorReport.insufficientFundBudgets.length}}
              />
            </Body>
          </Flex>
        )}
      </Flex>

      <H6 marginBottom={4}>
        <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.downloadReport.title" />
      </H6>

      <Button tertiary cta onClick={downloadCsvFile} marginLeft={2}>
        <Icon icon={IconTypes.downloadReport} size={22} />
        <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.downloadReport.cta.title" />
      </Button>

      <Flex gap="1rem" marginTop={5}>
        <Button large full onClick={onContinue}>
          <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.cta.title.continue" />
        </Button>
        <Button primary large full onClick={onUploadNewCsv}>
          <FormattedMessage id="wallet.adminReward.bulkRewards.errorModal.cta.title.upload" />
        </Button>
      </Flex>
    </GenericModal>
  )
}

export default CSVErrorReportModal
